import {
  INTERP_DELAY_MS,
  MAX_CMDS_PER_INPUT,
  TICK_DT,
  clampPitch,
  type InputCmd,
  type Vec3,
} from "@rietz/shared";
import type { DebugOverlay } from "./debug/overlay.js";
import type { DebugTools } from "./debug/tools.js";
import { lookAngles } from "./game/killcam.js";
import { applyLeanOffset, easeLean, leanRoll } from "./game/lean.js";
import type { RemoteAvatars } from "./game/remotes.js";
import type { WorldView } from "./game/world.js";
import type { Input } from "./input/input.js";
import type { NetClock } from "./net/clock.js";
import type { EntityInterpolator } from "./net/interpolation.js";
import type { Prediction } from "./net/prediction.js";
import type { GameSocket } from "./net/socket.js";

/**
 * The single requestAnimationFrame driver. Simulation runs on a fixed-step
 * accumulator at exactly `TICK_DT` (`stepMovement` is only deterministic
 * there); rendering runs at display rate, interpolating position between the
 * last two simulated states while the camera's ORIENTATION is taken raw from
 * the mouse — aiming latency is the one thing players feel instantly.
 */

const TICK_MS = TICK_DT * 1000;

/**
 * Hard cap on the accumulator, or a backgrounded tab would simulate every
 * missed tick in one frame. Dropping the time is correct: the server will
 * rebase us.
 */
const MAX_ACCUMULATED_MS = 250;

/**
 * The combat/effects layer, injected rather than imported. `GameLoop` owns the
 * frame's TIMING, not its content: it knows when the rendered position is final
 * and the remotes are posed, which is the moment weapons, effects and audio
 * need.
 */
export interface CombatSystems {
  /**
   * Once per rendered frame, after remotes are posed and before the draw.
   * `feet` and `eyeHeight` are the pair the camera was placed with.
   */
  update(dtMs: number, nowMs: number, feet: Readonly<Vec3>, eyeHeight: number): void;
}

export interface GameLoopDeps {
  view: WorldView;
  input: Input;
  prediction: Prediction;
  remotes: RemoteAvatars;
  clock: NetClock;
  interpolator: EntityInterpolator;
  overlay: DebugOverlay;
  /** Keybound debug layers (F3–F6). Omit to run without any of it. */
  debug?: DebugTools;
  /** `null` in offline mode: everything runs, nothing is sent. */
  socket: GameSocket | null;
  /** Weapons, effects and audio. Omit to run the loop without any of it. */
  combat?: CombatSystems;
  /**
   * Recoil and sway, as two angles added to the mouse — the same pair
   * `fireControl.aimDirection` adds, which is why the sights can move at all.
   * Omit it and the camera is the raw mouse.
   */
  aim?: { readonly offsetPitch: number; readonly offsetYaw: number };
  /** Reported in the HUD. */
  statusText: () => string;
}

export class GameLoop {
  /**
   * Simulate and render, but send nothing. The walkable hideout runs with the
   * socket OPEN (the menu talks over it) on a map the server has never heard
   * of, so `input` messages are DRAINED rather than skipped — produced,
   * applied, predicted, never sent. PINGS STILL GO OUT so the clock stays
   * synchronised.
   */
  localOnly = false;

  private readonly d: GameLoopDeps;

  private running = false;
  private rafId = 0;

  private lastFrameMs = 0;
  private accumulatorMs = 0;

  private seq = 0;

  /** Commands produced this frame, flushed to the socket before rendering. */
  private readonly outbox: InputCmd[] = [];

  /** Scratch — the render loop must not allocate. */
  private readonly tmpPos: Vec3 = { x: 0, y: 0, z: 0 };

  /**
   * The RENDERED lean: `input.lean` eased toward, −1…+1. Here rather than in
   * `Prediction` because the server hit-regs against the discrete bit on each
   * command; this is only the curve the camera travels.
   */
  private leanT = 0;

  /**
   * Camera override after a death: frozen at the killer's eye, looking at the
   * body. `null` when the camera is the player's own.
   */
  private killcam: { eye: Vec3; yaw: number; pitch: number; untilMs: number } | null = null;

  constructor(deps: GameLoopDeps) {
    this.d = deps;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastFrameMs = performance.now();
    this.accumulatorMs = 0;
    this.rafId = requestAnimationFrame(this.frame);
  }

  /**
   * Moves the local input sequence past a value the server has already seen — a
   * reconnect attaches a fresh counter to a PMC whose replay guard survived the
   * socket, and fast-forwarding makes the next command acceptable at once.
   * Never moves backwards.
   */
  syncInputSeq(lastAckSeq: number): void {
    if (Number.isSafeInteger(lastAckSeq) && lastAckSeq > this.seq) {
      this.seq = lastAckSeq;
    }
  }

  /**
   * Freezes the picture at `eye` looking at `lookAt` until `untilMs` (the
   * render clock). Only the CAMERA moves — the body, the audio listener and
   * everything predicted stay where the player fell.
   */
  setKillcam(eye: Readonly<Vec3>, lookAt: Readonly<Vec3>, untilMs: number): void {
    const { yaw, pitch } = lookAngles(eye, lookAt);
    this.killcam = { eye: { x: eye.x, y: eye.y, z: eye.z }, yaw, pitch, untilMs };
  }

  clearKillcam(): void {
    this.killcam = null;
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== 0) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") return;
    // rAF was throttled or stopped; start the next frame from a clean slate.
    this.lastFrameMs = performance.now();
    this.accumulatorMs = 0;
  };

  private readonly frame = (nowMs: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    const frameDtMs = Math.max(0, nowMs - this.lastFrameMs);
    this.lastFrameMs = nowMs;

    this.accumulatorMs = Math.min(
      this.accumulatorMs + frameDtMs,
      MAX_ACCUMULATED_MS,
    );

    // ---- Fixed-step simulation: input is never suppressed — every tick
    // produces
    // a command, and noclip rides INSIDE it as a button bit.
    const debug = this.d.debug;

    while (this.accumulatorMs >= TICK_MS) {
      this.accumulatorMs -= TICK_MS;
      this.seq += 1;
      const cmd = this.d.input.sampleCmd(this.seq);
      this.d.prediction.applyLocalCmd(cmd);
      this.outbox.push(cmd);
    }

    this.flushOutbox();
    this.maybePing(nowMs);

    // ---- Render ----------------------------------------------------------
    const alpha = this.accumulatorMs / TICK_MS;

    this.d.prediction.decaySmoothing(frameDtMs);
    const feet = this.d.prediction.sampleRenderPos(alpha, this.tmpPos);
    const eye = this.d.prediction.sampleRenderEyeHeight(alpha);

    // Eased on the RENDER clock: the peek is a smooth motion, and 30 Hz would
    // staircase the horizon roll.
    this.leanT = easeLean(this.leanT, this.d.input.lean, frameDtMs);

    const cam = this.d.view.camera;
    // Unconditionally: the camera is always the predicted eye, flying or
    // walking.
    cam.position.set(feet.x, feet.y + eye, feet.z);
    // ...and out to the side if peeking. Applied to the CAMERA, not `feet`,
    // which
    // stays the body's honest position for the combat systems and the audio
    // listener. The lean ignores collision — the head may reach through the
    // corner it peeks round, and the hitboxes go with it (`sim/hitbox.ts`).
    applyLeanOffset(cam.position, this.d.input.yaw, this.leanT);
    // Mouse plus aim offset, clamped as the input layer clamps the mouse. The
    // offset was written at the END of the previous frame, so it is one frame
    // stale — deliberately: updating aim before the camera would put a shot's
    // own kick into the frame that fired it. The third slot is the lean's ROLL
    // (rotation order "YXZ", so z is a roll about the view axis; `leanRoll`
    // owns
    // the sign).
    cam.rotation.set(
      clampPitch(this.d.input.pitch + (this.d.aim?.offsetPitch ?? 0)),
      this.d.input.yaw + (this.d.aim?.offsetYaw ?? 0),
      leanRoll(this.leanT),
    );
    // ...and then the killcam takes the camera outright, over the pose just
    // written. `feet`/`eye` below stay the player's own: the listener stays at
    // the body, only the picture moves.
    const kc = this.killcam;
    if (kc !== null) {
      if (nowMs >= kc.untilMs) this.killcam = null;
      else {
        cam.position.set(kc.eye.x, kc.eye.y, kc.eye.z);
        cam.rotation.set(kc.pitch, kc.yaw, 0);
      }
    }
    // The debug layers that track the local player. No-op, and allocation-free,
    // when nothing is on.
    debug?.update(frameDtMs, feet, eye);

    if (this.d.clock.synced) {
      const renderMs = this.d.clock.estServerNowMs(nowMs) - INTERP_DELAY_MS;
      // `frameDtMs` drives the avatars' procedural animation blends only.
      // `nowMs` is the wall clock the bark bubbles expire on.
      this.d.remotes.update(this.d.interpolator.sample(renderMs), frameDtMs, nowMs);
    }

    // After the remotes are posed (hit prediction tests the world on screen)
    // and
    // before the draw (this frame's tracer is visible in it).
    this.d.combat?.update(frameDtMs, nowMs, feet, eye);

    // The frame time goes in for the view's one render-clock effect, the rain.
    this.d.view.render(frameDtMs);
    this.updateOverlay(nowMs);
  };

  private flushOutbox(): void {
    if (this.outbox.length === 0) return;
    const socket = this.d.socket;
    if (!this.localOnly && socket !== null && socket.isOpen()) {
      // Several ticks can land in one frame on a slow display; the protocol
      // allows batching up to `MAX_CMDS_PER_INPUT` per message.
      for (let i = 0; i < this.outbox.length; i += MAX_CMDS_PER_INPUT) {
        socket.sendInput(this.outbox.slice(i, i + MAX_CMDS_PER_INPUT));
      }
    }
    this.outbox.length = 0;
  }

  private maybePing(nowMs: number): void {
    const socket = this.d.socket;
    if (socket === null || !socket.isOpen()) return;
    const cNow = this.d.clock.pingIfDue(nowMs);
    if (cNow !== null) socket.sendPing(cNow);
  }

  private updateOverlay(nowMs: number): void {
    if (!this.d.overlay.beginFrame(nowMs)) return;

    const state = this.d.prediction.state;
    const lastSnapAt = this.d.interpolator.lastSnapshotAtMs;

    this.d.overlay.render(
      {
        status: this.d.statusText(),
        pingMs: this.d.clock.rttMs,
        offsetMs: this.d.clock.serverWallOffsetMs,
        serverTick: this.d.clock.synced ? this.d.clock.estServerTick(nowMs) : 0,
        players: this.d.remotes.count,
        reconcileErrM: this.d.prediction.lastErrorM,
        correction: this.d.prediction.lastCorrection,
        snapshotAgeMs: lastSnapAt === 0 ? null : nowMs - lastSnapAt,
        pendingCmds: this.d.prediction.pendingCmds,
        pos: state.pos,
        stance: state.stance,
        speed: Math.hypot(state.vel.x, state.vel.z),
        stamina: state.stamina,
      },
      this.d.debug?.overlayExtras(state.pos),
    );
  }
}
